import { useCallback, useRef, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { DataTable, DataTableColumn } from "@/components/ui/data-table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { useItemsStore } from "./itemsStore";
import {
  Item,
  CreateItemParams,
  UpdateItemParams,
  ItemTypeLabel,
  CostingMethodEnum,
  CostingMethodLabel,
  CSV_IMPORT_TYPE,
} from "@forgepoint/shared";
import {
  Trash2,
  Loader,
  Layers,
  Upload,
  Archive,
  ArchiveRestore,
} from "lucide-react";
import { toast } from "sonner";
import { usePaginatedEntitySearch } from "@/lib/usePaginatedEntitySearch";
import { useRecordQuotesStore } from "@/modules/quotes/quotesStore";
import { DeleteConfirmationDialog } from "@/components/DeleteConfirmationDialog";
import ItemEditor from "./ItemEditor";
import { getUnitCostForQuantity } from "./utils";
import CsvImportSheet from "@/modules/csvImports/CsvImportSheet";
import { DataTableBadge } from "@/components/ui/data-table-badge";
import type { ItemType } from "@forgepoint/shared";

const ITEM_TYPE_COLOR: Record<ItemType, "blue" | "orange" | "emerald"> = {
  MANUFACTURED: "blue",
  PURCHASED: "orange",
  MATERIAL: "emerald",
};

/** Props for {@link ItemsController}. */
interface ItemsControllerProps {
  /** Whether the current user may create, edit, or delete items. */
  canEdit: boolean;
}

/**
 * Lists items and, when the user holds `items.edit`, exposes create, edit,
 * import, and delete controls.
 * @param props - Whether the user may mutate items
 * @returns The items list view
 */
export default function ItemsController({ canEdit }: ItemsControllerProps) {
  const {
    items,
    listLoading,
    listError,
    itemLoadingStates,
    fetchItems,
    createItem,
    updateItem,
    deleteItem,
  } = useItemsStore();
  const { countQuotesByItem } = useRecordQuotesStore();

  const [showArchived, setShowArchived] = useState(false);
  const showArchivedRef = useRef(false);

  const fetchItemsWithArchived = useCallback(
    (params: { search?: string; limit: number; offset: number }) =>
      fetchItems({
        ...params,
        includeArchived: showArchivedRef.current || undefined,
      }),
    [fetchItems],
  );

  const {
    entitiesArray: itemsArray,
    handleSearch,
    setDisplayedEntityIds: setDisplayedItemIds,
    page,
    fetchPage,
    refetchAfterRemoval,
    pagination,
  } = usePaginatedEntitySearch({
    entities: items,
    fetchEntities: fetchItemsWithArchived,
  });

  const [isCreateSheetOpen, setIsCreateSheetOpen] = useState(false);
  const [isImportSheetOpen, setIsImportSheetOpen] = useState(false);
  const [editingItemId, setEditingItemId] = useState<string | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<Item | null>(null);
  const [itemToArchive, setItemToArchive] = useState<Item | null>(null);
  const [archiveQuoteUsage, setArchiveQuoteUsage] = useState<number | null>(
    null,
  );

  const handleToggleArchived = (checked: boolean) => {
    showArchivedRef.current = checked;
    setShowArchived(checked);
    fetchPage(0);
  };

  const handleCreate = () => {
    setIsCreateSheetOpen(true);
  };

  /**
   * Refreshes the current page after a CSV import completes.
   */
  const handleImportComplete = async () => {
    await fetchPage(page);
  };

  const handleEdit = (item: Item) => {
    setEditingItemId(item.id);
  };

  const handleDelete = (item: Item) => {
    setItemToDelete(item);
    setIsDeleteDialogOpen(true);
  };

  const handleArchive = (item: Item) => {
    setArchiveQuoteUsage(null);
    setItemToArchive(item);
    countQuotesByItem(item.id).then(setArchiveQuoteUsage);
  };

  const handleConfirmArchive = async () => {
    if (!itemToArchive) {
      return;
    }

    const archivingItem = itemToArchive;
    setItemToArchive(null);

    const { item, error } = await updateItem(archivingItem.id, {
      archived: true,
    });
    if (item) {
      toast.success("Item archived");
      await (showArchived ? fetchPage(page) : refetchAfterRemoval());
    } else {
      toast.error(error ?? "Failed to archive item");
    }
  };

  const handleUnarchive = async (itemToUnarchive: Item) => {
    const { item, error } = await updateItem(itemToUnarchive.id, {
      archived: false,
    });
    if (item) {
      toast.success("Item unarchived");
      await fetchPage(page);
    } else {
      toast.error(error ?? "Failed to unarchive item");
    }
  };

  const handleViewArchiveItem = () => {
    if (!itemToArchive) {
      return;
    }
    setEditingItemId(itemToArchive.id);
    setItemToArchive(null);
  };

  const handleSaveCreate = async (
    data: CreateItemParams,
  ): Promise<true | string> => {
    const { item, error } = await createItem(data);
    if (item) {
      setDisplayedItemIds((prev) => [item.id, ...prev]);
      return true;
    }
    return error ?? "Failed to create item";
  };

  const handleSaveEdit = async (
    data: UpdateItemParams,
  ): Promise<true | string> => {
    if (!editingItemId) {
      return "No item selected for editing";
    }
    const { item, error } = await updateItem(editingItemId, data);
    return item ? true : (error ?? "Failed to update item");
  };

  const handleConfirmDelete = async () => {
    if (!itemToDelete) {
      return;
    }

    const result = await deleteItem(itemToDelete.id);
    if (result.success) {
      toast.success("Item deleted successfully");
      setIsDeleteDialogOpen(false);
      setItemToDelete(null);
      await refetchAfterRemoval();
    } else {
      toast.error(result.error || "Failed to delete item");
    }
  };

  const columns: DataTableColumn<Item>[] = [
    {
      key: "name",
      header: "Name / Item #",
      type: "string",
      format: (_value: unknown, item: Item) => (
        <div className="flex items-center gap-2">
          <span>{item.name}</span>
          {item.archivedAt && (
            <DataTableBadge color="amber">Archived</DataTableBadge>
          )}
        </div>
      ),
    },
    {
      key: "revision",
      header: "Revision",
      type: "string",
      width: 100,
    },
    {
      key: "type",
      header: "Type",
      type: "badge",
      width: 120,
      format: (_value: unknown, item: Item) => (
        <DataTableBadge color={ITEM_TYPE_COLOR[item.type]}>
          {ItemTypeLabel[item.type]}
        </DataTableBadge>
      ),
    },
    {
      key: "costingMethod",
      header: "Costing Method",
      type: "badge",
      width: 150,
      format: (_value: unknown, item: Item) => (
        <DataTableBadge>
          {CostingMethodLabel[item.costingMethod]}
        </DataTableBadge>
      ),
    },
    {
      key: "unit",
      header: "Unit",
      type: "string",
      width: 80,
    },
    {
      key: "unitCost",
      header: "Unit Cost",
      width: 120,
      align: "right",
      format: (_value: unknown, item: Item) => {
        if (item.costingMethod === CostingMethodEnum.BOM) {
          return <span className="text-muted-foreground">-</span>;
        }

        const baseCost = getUnitCostForQuantity(item, 1);
        const hasQuantityBasedPricing = (item.costBreaks?.length ?? 0) > 1;

        if (!baseCost) {
          return <span className="text-muted-foreground">-</span>;
        }

        return (
          <div className="flex items-center justify-end gap-1.5">
            <span>
              {new Intl.NumberFormat("en-US", {
                style: "currency",
                currency: "USD",
              }).format(Number(baseCost))}
            </span>
            {hasQuantityBasedPricing && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger>
                    <Layers className="h-3 w-3 text-muted-foreground" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Quantity-based pricing</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
        );
      },
    },
    {
      key: "createdAt",
      header: "Created",
      type: "date",
      width: 120,
    },
    {
      key: "syncStatus",
      header: "Sync",
      width: 90,
      type: "string",
      className: "hidden md:table-cell",
      format: (_value: unknown, item: Item) =>
        item.externalMappings?.length ? (
          <Badge variant="secondary">Synced</Badge>
        ) : (
          "-"
        ),
    },
    {
      key: "actions",
      header: "",
      width: 100,
      format: (_value: unknown, item: Item) => {
        if (!canEdit) {
          return null;
        }

        const loadingState = itemLoadingStates.get(item.id);
        const isDeleting = loadingState?.isLoadingDelete;
        const isSaving = loadingState?.isLoadingSave;

        return (
          <div className="flex items-center justify-end">
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                if (item.archivedAt) {
                  handleUnarchive(item);
                  return;
                }
                handleArchive(item);
              }}
              disabled={isSaving}
              title={item.archivedAt ? "Unarchive item" : "Archive item"}
            >
              {isSaving ? (
                <Loader className="h-4 w-4 animate-spin" />
              ) : item.archivedAt ? (
                <ArchiveRestore className="h-4 w-4" />
              ) : (
                <Archive className="h-4 w-4" />
              )}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                handleDelete(item);
              }}
              disabled={isDeleting}
              title="Delete item"
            >
              {isDeleting ? (
                <Loader className="h-4 w-4 animate-spin" />
              ) : (
                <Trash2 className="h-4 w-4" />
              )}
            </Button>
          </div>
        );
      },
    },
  ];

  if (listError) {
    return (
      <div className="p-8 text-destructive">
        Error loading items: {listError}
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <DataTable
        columns={columns}
        data={itemsArray}
        searchPlaceholder="Search items..."
        className="h-full"
        isLoading={listLoading && itemsArray.length === 0}
        onCreate={canEdit ? handleCreate : undefined}
        createButtonText="New Item"
        onClickRow={canEdit ? handleEdit : undefined}
        onChangeSearch={handleSearch}
        pagination={pagination}
        toolbarActions={
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Switch
                id="show-archived-items"
                checked={showArchived}
                onCheckedChange={handleToggleArchived}
              />
              <Label
                htmlFor="show-archived-items"
                className="text-sm whitespace-nowrap"
              >
                Show Archived
              </Label>
            </div>
            {canEdit && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsImportSheetOpen(true)}
              >
                <Upload className="h-4 w-4 mr-1" />
                Import
              </Button>
            )}
          </div>
        }
      />

      <CsvImportSheet
        importType={CSV_IMPORT_TYPE.ITEMS}
        open={isImportSheetOpen}
        onOpenChange={setIsImportSheetOpen}
        onImportComplete={handleImportComplete}
      />

      {/* Create Item Sheet */}
      <ItemEditor
        open={isCreateSheetOpen}
        onOpenChange={setIsCreateSheetOpen}
        onSave={handleSaveCreate}
        mode="create"
      />

      {/* Edit Item Sheet */}
      {editingItemId && (
        <ItemEditor
          open={!!editingItemId}
          onOpenChange={(open) => !open && setEditingItemId(null)}
          itemId={editingItemId}
          onSave={handleSaveEdit}
          mode="edit"
        />
      )}

      <DeleteConfirmationDialog
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
        onConfirm={handleConfirmDelete}
        entityType="Item"
        itemName={itemToDelete?.name}
      />

      <AlertDialog
        open={!!itemToArchive}
        onOpenChange={(open) => !open && setItemToArchive(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Archive Item</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to archive &quot;{itemToArchive?.name}
              &quot;? Archived items are hidden from item lists and pickers. You
              can unarchive it later.
            </AlertDialogDescription>
            {archiveQuoteUsage !== null && archiveQuoteUsage > 0 && (
              <AlertDialogDescription className="rounded-md border border-amber-500/50 bg-amber-500/10 p-3 text-amber-700 dark:text-amber-400">
                This item is used in {archiveQuoteUsage}{" "}
                {archiveQuoteUsage === 1 ? "quote" : "quotes"}. Archiving does
                not remove it from existing quotes — create a new quote revision
                and let your customer know. Open the item to review its
                Associated Quotes.
              </AlertDialogDescription>
            )}
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            {archiveQuoteUsage !== null && archiveQuoteUsage > 0 && (
              <Button variant="outline" onClick={handleViewArchiveItem}>
                View Item
              </Button>
            )}
            <AlertDialogAction
              onClick={handleConfirmArchive}
              disabled={archiveQuoteUsage === null}
            >
              Archive{" "}
              {archiveQuoteUsage === null && (
                <Loader className="h-4 w-4 animate-spin" />
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
